import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DestinationStay {
  id: string;
  slug: string;
  name: string;
  category: string | null;
  shortLocation: string | null;
  fullAddress: string | null;
  description: string | null;
  whyWePickedIt: string | null;
  priceFrom: number | null;
  currencyCode: string | null;
  reviewScore: number | null;
  reviewCount: number | null;
  instagramUrl: string | null;
  websiteUrl: string | null;
  affiliateUrl: string | null;
  bookingButtonText: string | null;
  heroImageUrl: string | null;
  thumbnailImageUrl: string | null;
  tags: string[];
  bestFor: string[];
  facilities: string[];
  inclusions: string[];
  isFeatured: boolean;
  isSwellBreaksPick: boolean;
  galleryImages: { url: string; alt: string | null }[];
}

interface DbStay {
  id: string;
  slug: string;
  name: string;
  stay_category: string | null;
  short_location: string | null;
  full_address: string | null;
  description: string | null;
  why_we_picked_it: string | null;
  price_from: number | null;
  currency_code: string | null;
  review_score: number | null;
  review_count: number | null;
  instagram_url: string | null;
  website_url: string | null;
  affiliate_url: string | null;
  booking_button_text: string | null;
  hero_image_url: string | null;
  thumbnail_image_url: string | null;
  tags: string[] | null;
  best_for: string[] | null;
  facilities: string[] | null;
  inclusions: string[] | null;
  is_featured: boolean | null;
  is_swell_breaks_pick: boolean | null;
  is_active: boolean | null;
  sort_order: number | null;
}

const adapt = (s: DbStay, images: DbStayImage[] = []): DestinationStay => ({
  id: s.id,
  slug: s.slug,
  name: s.name,
  category: s.stay_category,
  shortLocation: s.short_location,
  fullAddress: s.full_address,
  description: s.description,
  whyWePickedIt: s.why_we_picked_it,
  priceFrom: s.price_from,
  currencyCode: s.currency_code,
  reviewScore: s.review_score,
  reviewCount: s.review_count,
  instagramUrl: s.instagram_url && s.instagram_url !== 'NA' ? s.instagram_url : null,
  websiteUrl: s.website_url && s.website_url !== 'NA' ? s.website_url : null,
  affiliateUrl: s.affiliate_url && s.affiliate_url !== 'NA' ? s.affiliate_url : null,
  bookingButtonText: s.booking_button_text || 'View Stay',
  heroImageUrl: s.hero_image_url,
  thumbnailImageUrl: s.thumbnail_image_url,
  tags: s.tags ?? [],
  bestFor: s.best_for ?? [],
  facilities: s.facilities ?? [],
  inclusions: s.inclusions ?? [],
  isFeatured: !!s.is_featured,
  isSwellBreaksPick: !!s.is_swell_breaks_pick,
  galleryImages: images
    .filter((i) => i.hero_image_url && i.hero_image_url !== s.hero_image_url)
    .map((i) => ({ url: i.hero_image_url as string, alt: i.alt_text })),
});

interface DbStayImage {
  id: string;
  stay_id: string;
  image_type: string | null;
  alt_text: string | null;
  sort_order: number | null;
  hero_image_url: string | null;
  thumbnail_image_url: string | null;
}

export const useDestinationStays = (destinationId: string | undefined) => {
  return useQuery({
    queryKey: ['destination-stays', destinationId],
    enabled: !!destinationId,
    queryFn: async (): Promise<DestinationStay[]> => {
      const { data, error } = await supabase
        .from('destination_stays')
        .select('*')
        .eq('destination_id', destinationId!)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      const stays = (data as unknown as DbStay[]) ?? [];
      if (stays.length === 0) return [];

      const stayIds = stays.map((s) => s.id);
      const { data: imgData, error: imgErr } = await supabase
        .from('destination_stay_images' as never)
        .select('*')
        .in('stay_id', stayIds)
        .order('sort_order', { ascending: true });
      if (imgErr) throw imgErr;
      const imagesByStay = new Map<string, DbStayImage[]>();
      ((imgData as unknown as DbStayImage[]) ?? []).forEach((img) => {
        const arr = imagesByStay.get(img.stay_id) ?? [];
        arr.push(img);
        imagesByStay.set(img.stay_id, arr);
      });

      return stays.map((s) => adapt(s, imagesByStay.get(s.id) ?? []));
    },
  });
};
